import { api } from './client';

// Endpoints do papel timbrado pessoal (cabeçalho/rodapé/marca d'água + logo).
// Módulo separado de propósito — não cresce o client.ts monolítico.

export interface TimbradoConfig {
  header: string | null;
  footer: string | null;
  watermark: string | null;
  has_logo: boolean;
  /** Logo embutido como data URI (data:image/...;base64,...) para preview. */
  logo_url: string | null;
  /** As faixas prontas do papel (o desenho do cabeçalho e o do rodapé), como data URI. */
  header_image_url?: string | null;
  footer_image_url?: string | null;
  /** A página das peças novas do editor (papel, orientação, margens), quando guardada. */
  pagina?: PaginaPadraoApi | null;
  /** Só no resolvido: o padrão do escritório vence o de cada membro? `null` sem escritório. */
  escritorio_prevalece?: boolean | null;
}

/** Os nomes são os do documento do editor (`DocumentoForense`). */
export interface PaginaPadraoApi {
  papel: 'a4' | 'oficio' | 'carta';
  orientacao: 'retrato' | 'paisagem';
  margens: { superior: number; inferior: number; esquerda: number; direita: number };
  nomeMargens: string;
}

export interface TimbradoConfigInput {
  header?: string | null;
  footer?: string | null;
  watermark?: string | null;
}

/**
 * De quem é o timbrado: o pessoal (`me`) ou o de um escritório (a aba "Peças" dele).
 * As rotas são as mesmas, sobre um dono ou outro.
 */
export type AlvoDoTimbrado = 'me' | { escritorio: string };

const base = (alvo: AlvoDoTimbrado) => (alvo === 'me' ? '/timbrado/me' : `/timbrado/escritorio/${alvo.escritorio}`);

/** Sem argumento: é `queryFn` (o react-query passa o contexto como 1º argumento). */
export async function fetchTimbrado(): Promise<TimbradoConfig> {
  return fetchTimbradoDe('me');
}

export async function fetchTimbradoDe(alvo: AlvoDoTimbrado): Promise<TimbradoConfig> {
  const { data } = await api.get<TimbradoConfig>(base(alvo));
  return data;
}

/**
 * Timbrado RESOLVIDO (com precedência do escritório) — o mesmo que o render do
 * PDF aplica. Usado pela "casca" do editor de peticionamento para mostrar onde
 * entram cabeçalho, logo e rodapé, sem divergir do PDF final.
 */
export async function fetchTimbradoResolvido(): Promise<TimbradoConfig> {
  const { data } = await api.get<TimbradoConfig>('/timbrado/me/resolvido');
  return data;
}

export async function saveTimbrado(input: TimbradoConfigInput, alvo: AlvoDoTimbrado = 'me'): Promise<TimbradoConfig> {
  const { data } = await api.put<TimbradoConfig>(base(alvo), input);
  return data;
}

export async function uploadTimbradoLogo(file: File, alvo: AlvoDoTimbrado = 'me'): Promise<TimbradoConfig> {
  const form = new FormData();
  form.append('arquivo', file);
  const { data } = await api.post<TimbradoConfig>(`${base(alvo)}/logo`, form);
  return data;
}

/** As faixas prontas do papel: campo ausente não mexe, `null` remove. */
export async function saveTimbradoImagens(
  input: { cabecalho?: string | null; rodape?: string | null },
  alvo: AlvoDoTimbrado = 'me',
): Promise<TimbradoConfig> {
  const { data } = await api.put<TimbradoConfig>(`${base(alvo)}/imagens`, input);
  return data;
}

export async function removeTimbradoLogo(alvo: AlvoDoTimbrado = 'me'): Promise<TimbradoConfig> {
  const { data } = await api.delete<TimbradoConfig>(`${base(alvo)}/logo`);
  return data;
}

/** A página das peças novas; `null` volta ao padrão do app. */
export async function saveTimbradoPagina(pagina: PaginaPadraoApi | null, alvo: AlvoDoTimbrado = 'me'): Promise<TimbradoConfig> {
  const { data } = await api.put<TimbradoConfig>(`${base(alvo)}/pagina`, { pagina });
  return data;
}
